import type { Op } from 'quill/core';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import InputTabs from '../../components/InputTabs';
import LargeTooltip from '../../components/LargeTooltip';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import SVGIcon from '../../components/SVGIcon';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import infoIcon from '../../icons/non-prefilled/16x16/information.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import mentionIcon from '../../icons/non-prefilled/16x16/mention.svg';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import addTestId from '../../utils/addTestId';
import { insertPrefix, insertText } from '../../utils/quillHelpers';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import MagneticWallItem from '../MagneticWallItem';
import { ANNOTATION_CREATION_ID } from '../PendingMagneticWallItemProvider';
import useShouldShowFullPageSheet from '../comment/useShouldShowFullPageSheet';
import isContentEmpty from '../utils/isContentEmpty';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import * as styles from './AnnotationCreate.module.scss';
import AnnotationIcon from './AnnotationIcon';
import AnnotationInput from './AnnotationInput';
import type { AnnotationState } from './useAnnotationState';

const UPDATE_ANNOTATION = gql(/** @complexity 2 */ `
  mutation updateAnnotation($id: ID!, $content: Json, $link: String, $linkText: String) {
    updateAnnotation(id: $id, content: $content, link: $link, linkText: $linkText) {
      id 
      content
      link
      linkText
    }
  }
`);

type EditingTarget = 'link' | 'text';

const SAVE_CLICK_EVENTS = new WeakSet<Event>();

interface AnnotationLinkEditorProps {
  onSave: () => void;
  onCancel: () => void;
  link: string;
  setLink: (link: string) => void;
  text: string;
  setText: (text: string) => void;
  isOpen: boolean;
  children: React.ReactNode;
}

const AnnotationLinkEditor = ({
  onSave,
  onCancel,
  link,
  setLink,
  text,
  setText,
  isOpen,
  children,
}: AnnotationLinkEditorProps) => {
  const [editingTarget, setEditingTarget] = useState<EditingTarget>('link');
  const [initialLink, setInitialLink] = useState(link);
  const [initialText, setInitialText] = useState(text);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const handleCancel = () => {
    onCancel();
    setLink(initialLink);
    setText(initialText);
  };

  const handleSave = () => {
    onSave();
    setInitialLink(link);
    setInitialText(text);
  };

  return (
    <PopOverPrimitive
      trigger="click"
      open={isOpen}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) {
          handleCancel();
        }
      }}
      placement={isMobile ? 'top-start' : 'left-start'}
      offset={isMobile ? 5 : 14}
    >
      <ClickOutsideHandler
        onClickOutside={(e) => {
          if (!SAVE_CLICK_EVENTS.has(e) && isOpen) {
            handleCancel();
          }
        }}
      >
        <PopOverPrimitive.Trigger preventCloseOnClickOutside asChild>
          {children}
        </PopOverPrimitive.Trigger>
        <PopOverPrimitive.Floating>
          <PopOverPrimitive.Content
            {...addTestId('annotationLinkEditor')}
            className={styles.linkEditorPopOver}
          >
            <InputTabs.Root width={290}>
              <InputTabs.List>
                <InputTabs.TabButton
                  active={editingTarget === 'link'}
                  onClick={() => setEditingTarget('link')}
                  aria-label="Edit link"
                >
                  Link
                </InputTabs.TabButton>
                <InputTabs.TabButton
                  active={editingTarget === 'text'}
                  onClick={() => setEditingTarget('text')}
                  aria-label="Edit text"
                >
                  Text
                </InputTabs.TabButton>
                {link && (
                  <InputTabs.RightButton
                    onClick={() => {
                      setLink('');
                      setText('');
                      handleSave();
                    }}
                  >
                    Remove
                  </InputTabs.RightButton>
                )}
              </InputTabs.List>
              <InputTabs.Input
                onSave={handleSave}
                onEscape={handleCancel}
                placeholder={
                  editingTarget === 'link' ? 'Paste a link' : 'Paste a text'
                }
                value={editingTarget === 'text' ? text : link}
                onChange={(value) => {
                  if (editingTarget === 'text') {
                    setText(value);
                  } else {
                    setLink(value);
                  }
                }}
                saveDisabled={
                  !link ||
                  (editingTarget === 'link'
                    ? link === initialLink
                    : text === initialText)
                }
                aria-label={editingTarget === 'text' ? 'Text' : 'Link'}
              />
            </InputTabs.Root>
          </PopOverPrimitive.Content>
        </PopOverPrimitive.Floating>
      </ClickOutsideHandler>
    </PopOverPrimitive>
  );
};

interface AnnotationCreateProps {
  annotationId: string;
  onSaved(): void;
  onCancel: () => void;
  annotationState: AnnotationState;
  updateAnnotationState: (value: Partial<AnnotationState>) => void;
  createAnnotation: ({
    content,
    sanitizedLink,
    linkText,
  }: {
    content: Op[];
    sanitizedLink: string | null;
    linkText: string;
  }) => void;
}

const AnnotationCreate = ({
  annotationId,
  onSaved,
  annotationState,
  updateAnnotationState,
  onCancel,
  createAnnotation,
}: AnnotationCreateProps) => {
  const [isLinkEditorOpen, setIsLinkEditorOpen] = useState(false);
  const isNewAnnotation = annotationId === ANNOTATION_CREATION_ID;
  const { quill, ancestorQuill } = useQuill();
  const sync = useAppSelector((state) => state.editor.sync);
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const sanitizedLink = sanitizeAnnotationLink(annotationState.link);

  const [updateAnnotation] = useMutation(UPDATE_ANNOTATION);

  const handleSave = () => {
    if (isNewAnnotation) {
      if (!sync) throw new Error('sync not inited');
      if (!ancestorQuill) throw new Error('quill not inited');

      createAnnotation({
        content: annotationState.content,
        sanitizedLink,
        linkText: annotationState.linkText,
      });
    } else {
      const optimisticAnnotation = {
        id: annotationId,
        content: annotationState.content,
        link: sanitizedLink,
        linkText: annotationState.linkText,
        __typename: 'Annotation' as const,
      };

      updateAnnotation({
        variables: {
          id: annotationId,
          content: JSON.stringify(annotationState.content),
          link: sanitizedLink,
          linkText: annotationState.linkText,
        },
        optimisticResponse: {
          __typename: 'RootMutationType',
          updateAnnotation: optimisticAnnotation,
        },
      });
    }
    onSaved();
  };

  const shouldShowFullPageSheet = useShouldShowFullPageSheet();

  const hasAutoFocus =
    (!shouldShowFullPageSheet || isNewAnnotation) &&
    (readOnly || !quill?.hasFocus());

  return (
    <div className={styles.container}>
      <MagneticWallItem.WithAvatar avatar={<AnnotationIcon />}>
        <div className={styles.inputContainer}>
          <AnnotationInput
            onChange={(content) => updateAnnotationState({ content })}
            autoFocus={hasAutoFocus}
            initialContent={annotationState.content}
            quill={quill}
            onSave={handleSave}
          />
        </div>
      </MagneticWallItem.WithAvatar>
      <div className={styles.actionsContainer}>
        <MagneticWallItem.ActionContainer>
          <AnnotationLinkEditor
            onSave={() => {
              setIsLinkEditorOpen(false);
            }}
            onCancel={() => {
              setIsLinkEditorOpen(false);
            }}
            link={annotationState.link}
            setLink={(link) => updateAnnotationState({ link })}
            text={annotationState.linkText}
            setText={(linkText) => updateAnnotationState({ linkText })}
            isOpen={isLinkEditorOpen}
          >
            <MagneticWallItem.IconButton
              icon={linkIcon}
              active={isLinkEditorOpen}
              highlighted={!!sanitizedLink}
              onClick={() => setIsLinkEditorOpen(true)}
              tooltip={
                sanitizedLink ? 'Edit reference link' : 'Add reference link'
              }
            />
          </AnnotationLinkEditor>

          <MagneticWallItem.EmojiPickerButton
            onPick={(emoji) => {
              if (quill) {
                insertText(quill, emoji);
              }
            }}
          />
          {currentUser.type === UserType.Visitor ? null : (
            <MagneticWallItem.IconButton
              icon={mentionIcon}
              onClick={() => {
                if (!quill) return;
                insertPrefix(quill, '@');
              }}
              tooltip="Mention someone"
            />
          )}

          <div className={styles.submitButtonGroupContainer}>
            <LargeTooltip
              placement="bottom"
              content="Use annotations to add extra information or cite references"
            >
              <div className={styles.infoIconContainer}>
                <SVGIcon size={16}>{infoIcon}</SVGIcon>
              </div>
            </LargeTooltip>
            <MagneticWallItem.ButtonGroup>
              {!isNewAnnotation && (
                <MagneticWallItem.SecondaryButton onClick={onCancel}>
                  Cancel
                </MagneticWallItem.SecondaryButton>
              )}

              <MagneticWallItem.SubmitButton
                disabled={
                  isContentEmpty(annotationState.content) && !sanitizedLink
                }
                onMouseDown={(e) => {
                  SAVE_CLICK_EVENTS.add(e.nativeEvent);
                }}
                onTouchStart={(e) => {
                  SAVE_CLICK_EVENTS.add(e.nativeEvent);
                }}
                onClick={handleSave}
              >
                {isNewAnnotation ? 'Annotate' : 'Save'}
              </MagneticWallItem.SubmitButton>
            </MagneticWallItem.ButtonGroup>
          </div>
        </MagneticWallItem.ActionContainer>
      </div>
    </div>
  );
};

export default withQuillContextProvider(AnnotationCreate);
